import { IconButton, mergeStyles, TooltipHost } from "@fluentui/react";
import { getNextColor, IChartProps, ILineChartDataPoint } from "@fluentui/react-charting";
import { useId } from "@fluentui/react-hooks";
import { Triangle12Filled, TriangleDown12Filled } from "@fluentui/react-icons";
import React, { useLayoutEffect, useRef, useState } from "react";
import { LineChartComponent } from "../../../ChartComponents/LineChartFluent";
import { CarouselProps } from "../../../interfaces/CarouselProps.interfaces";
import { IChartVariances } from "../../../interfaces/chart-props.interfaces";
import { IProductKPI } from "../../../interfaces/customer-product-v2.interfaces";
import CustomCard from "../../../SharedComponents/CustomCard";
import FlagIndicatorsRow from "../../../SharedComponents/FlagIndicatorsRow";
import { CUSTOM_CARD } from "../../../SharedComponents/Styles/CustomCard.styles";
import { CUSTOMER_VIEW_BREAKPOINTS } from "../CustomerView.constants";
import ExploreFeatures from "../ExploreFeatures/ExploreFeatures";
import * as styles from "./ProductTelemetryChart.styles";

const breakpoints = CUSTOMER_VIEW_BREAKPOINTS;
const defaultStyles = CUSTOM_CARD.CUSTOM_CARD_DEFAULT_STYLES;

// TODO(UI mock): temporary flag to preview the latest-month (till-date) point on the chart.
// When true, the current month's till-date value (from variance.nextValue) is appended as the
// right-most point so we can see how the trend line looks with the latest data included.
const SHOW_LATEST_MONTH_MOCK_POINT = true;

// Families whose series are filtered by the selected granularity (Daily/Weekly/Monthly).
const GRANULARITY_FILTERED_FAMILIES = ["Microsoft Purview Consumption", "Microsoft Sentinel", "Security Copilot"];

// Parses a display-formatted metric value (e.g. "8.42M", "322.65K", "54.70") into a number.
const parseFormattedMetricValue = (formatted?: string): number | null => {
  if (!formatted) return null;
  const match = formatted.trim().replace(/,/g, "").match(/^(-?\d+(?:\.\d+)?)\s*([KMB]?)$/i);
  if (!match) return null;
  const suffix = match[2].toUpperCase();
  const multiplier = suffix === "K" ? 1e3 : suffix === "M" ? 1e6 : suffix === "B" ? 1e9 : 1;
  return parseFloat(match[1]) * multiplier;
};

// Formats a date for the chart callout, e.g. "9 Jul 2026".
const formatCalloutDate = (date: Date): string =>
  `${date.getUTCDate()} ${date.toLocaleString("en-US", { month: "short", timeZone: "UTC" })} ${date.getUTCFullYear()}`;

export const ProductTelemetryChart: React.FC<CarouselProps> = ({
  carouseldata,
  granularity,
  application,
  selectedFamily,
  tenantId,
  disableVariances = false,
  disableFlagIndicator = false,
  disableExploreFeaturesButton = false
}) => {
  const tooltipId = useId("tooltip");
  const [cardWidth, setCardWidth] = useState<number | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const calculateWidth = () => {
      let itemsPerRow = 1;

      for (const bp of breakpoints) {
        if (window.innerWidth >= bp.width) {
          itemsPerRow = bp.items;
          break;
        }
      }

      if (containerRef.current) {
        const availableWidth = containerRef.current.offsetWidth;
        const newCardWidth = Math.floor(availableWidth / itemsPerRow) - 16;
        setCardWidth(newCardWidth);
      }
    };

    calculateWidth();
    window.addEventListener("resize", calculateWidth);
    return () => window.removeEventListener("resize", calculateWidth);
  }, []);

  const CardVariance = ({ variances }: { variances: IChartVariances[] }) => {
    const rootStyle = variances.length < 2 ? styles.rootVarianceStyleSingle : styles.rootVarianceStyle;

    return (
      <div style={{ ...rootStyle, width: cardWidth ?? 0 }}>
        {variances.map((v, idx) => (
          <div key={`cvd-${idx}`} className={mergeStyles(styles.varianceContainerStyle)}>
            <div
              style={{
                alignItems: "center",
                borderBottom: "1px solid #d5d5d580",
                color: getNextColor(idx),
                display: "flex",
                fontWeight: "bold",
                marginBottom: 2
              }}
            >
              <div className={mergeStyles(styles.varianceMetricNameStyle)} title={v.name}>
                {v.name}
              </div>
              {selectedFamily === "Microsoft Sentinel" ? (
                <span style={{ marginLeft: "4px", paddingTop: "2px", fontSize: "10px", color: "#666" }} title={v.description}>
                  {v.description || "(Total data ingested - GB/month)"}
                </span>
              ) : (
                <TooltipHost content={v.description || "Total data ingested - GB/month"} id={tooltipId}>
                  <IconButton iconProps={{ iconName: "Info" }} aria-label="Info" />
                </TooltipHost>
              )}
            </div>
            <div>This month (till date)</div>
            <div style={styles.currMonthValStyle}>{v.variance.nextValue || "0"}</div>
            <div style={v.variance.diffValue.includes("-") ? styles.varianceRedStyle : styles.varianceGreenStyle}>
              {v.variance.diffValue.includes("-") ? (
                <TriangleDown12Filled />
              ) : v.variance.diffValue !== "0" ? (
                <Triangle12Filled />
              ) : null}
              <div>
                {v.variance.diffValue || 0} ({v.variance.diffPerc || "0%"})
              </div>
            </div>
            <div>v/s previous month ({v.variance.prevValue || 0})</div>
          </div>
        ))}
      </div>
    );
  };

  const GetItemData = (item: IProductKPI, idx: number) => {
    const isGranularityFiltered = GRANULARITY_FILTERED_FAMILIES.includes(selectedFamily ?? "");
    // Only append the mock latest-month point for monthly trends (skip Daily/Weekly views).
    const shouldAppendLatestMonth = SHOW_LATEST_MONTH_MOCK_POINT && (!isGranularityFiltered || granularity === "Monthly");

    const filteredLineChartData = item.data?.map((metric, i) => {
      const filteredPoints = metric.data?.filter(point => {
        const isMatchGranularity = ["Microsoft Purview Consumption", "Microsoft Sentinel", "Security Copilot"].includes(selectedFamily ?? "")
          ? point.aggregationType === granularity
          : true;
        const matchApp = ["Microsoft Purview Consumption"].includes(selectedFamily ?? "") && application
          ? point.application === application
          : true;
        return isMatchGranularity && matchApp;
      }) || [];

      const mappedPoints: ILineChartDataPoint[] = filteredPoints.map(point => ({
        x: new Date(point.x),
        y: Number(point.y)
      }));

      // Append the current month's till-date value (mock/UI preview) as the right-most point.
      // The point is POSITIONED at the end of the current month (e.g. Jul 31) so it keeps the same
      // monthly grid spacing as the other end-of-month points (avoids overlapping x-axis labels),
      // but its callout label is overridden to show the ACTUAL till-date (e.g. "9 Jul 2026").
      if (shouldAppendLatestMonth && mappedPoints.length > 0) {
        const latestValue = parseFormattedMetricValue(metric.variance?.nextValue);
        const lastPoint = mappedPoints[mappedPoints.length - 1] as { x: Date; y: number };
        const tillDate = new Date();
        // Last day of the current month, in UTC (matches the end-of-month convention of the API data).
        const currentMonthEnd = new Date(Date.UTC(tillDate.getUTCFullYear(), tillDate.getUTCMonth() + 1, 0));
        const alreadyHasCurrentMonth =
          lastPoint.x.getUTCFullYear() === currentMonthEnd.getUTCFullYear() &&
          lastPoint.x.getUTCMonth() === currentMonthEnd.getUTCMonth();

        if (latestValue !== null && !alreadyHasCurrentMonth) {
          mappedPoints.push({
            x: currentMonthEnd,
            y: latestValue,
            xAxisCalloutData: formatCalloutDate(tillDate)
          });
        }
      }

      return {
        legend: `${metric.name}`,
        data: mappedPoints,
        color: getNextColor(i),
        lineOptions: {
          lineBorderWidth: "4"
        }
      };
    }) ?? [];

    const allDataPoints = filteredLineChartData.flatMap(line => line.data);
    if (allDataPoints.length === 0) return null;

    const chartData: IChartProps = {
      lineChartData: filteredLineChartData
    };
    const chartVariances: IChartVariances[] = item.data
      .filter(metric => {
        const hasMonthlyData = metric.data?.some(point => {
          const isMatchGranularity = ["Microsoft Purview Consumption", "Microsoft Sentinel", "Security Copilot"].includes(selectedFamily ?? "")
            ? point.aggregationType === "Monthly"
            : true;

          const matchApp = ["Microsoft Purview Consumption"].includes(selectedFamily ?? "") && application
            ? point.application === application
            : true;

          return isMatchGranularity && matchApp;
        });

        return hasMonthlyData;
      })
      .map(({ description, name, variance }) => ({
        description,
        name,
        variance
      }));

    return (
      <div ref={containerRef} style={styles.cardContainerStyle(cardWidth)} key={idx}>
        <CustomCard
          cardHeaderTitle={item.name}
          exploreFeaturesComponent={
            !disableExploreFeaturesButton && (
              <ExploreFeatures productFamily={selectedFamily} productName={item.name} tenantId={tenantId} />
            )
          }
          flagIndicatorComponent={
            !disableFlagIndicator && (
              <div className={defaultStyles.cardFooterOuter}>
                <FlagIndicatorsRow isTCT={item.isTCT} isCAT={item.isCAT} hasEngagedAdmin={item.hasEngagedAdmin} />
              </div>
            )
          }
          kustoLink={item.kustoLink}
        >
          {!disableVariances && <CardVariance key={`CardVariance${idx}`} variances={chartVariances} />}
          <div style={{ backgroundColor: "white" }}>
            <LineChartComponent
              data={chartData}
              height={300}
              width={cardWidth}
              allowResize={true}
              selectedFamily={selectedFamily}
            />
          </div>
        </CustomCard>
      </div>
    );
  };

  return (
    <div ref={containerRef} style={styles.containerStyle}>
      {carouseldata.items.map((item, idx) => GetItemData(item, idx)).filter(Boolean)}
    </div>
  );
};
